import type {
  FundableProduct,
  FundInstructions,
  PendingFundRequest,
} from '../../types/finance';

/**
 * Contracted products that still have no funding reference. The Fund page
 * issues these on load (idempotent server-side), so a client never meets a
 * product they can fund but have no reference for.
 */
export function productsMissingReference(
  data: Pick<FundInstructions, 'fundable_products'>,
): FundableProduct[] {
  // A product held back for want of a source account would not show its reference if issued
  // again; the page asks for the account first.
  return (data.fundable_products ?? []).filter(
    (p) => !p.funding_reference?.trim() && !p.source_account_required && !p.tax_form_required,
  );
}

/** Currencies of the series that wait for a registered source account (S-27), each once. */
export function currenciesNeedingSourceAccount(
  data: Pick<FundInstructions, 'fundable_products' | 'pending_deposits'>,
): string[] {
  const needed = [
    ...(data.fundable_products ?? []).map((p) => (p.source_account_required ? p.currency_code : null)),
    ...(data.pending_deposits ?? []).map((d) => (d.source_account_required ? d.currency_code : null)),
  ];
  return [...new Set(needed.filter((c): c is string => !!c))];
}

/** The type of account a currency is sent from: pesos from a CLABE, dollars from a US account,
 *  Canadian dollars from a Canadian one. */
export function accountTypeForCurrency(currency: string): 'CLABE' | 'ABA' | 'CPA' | undefined {
  const types = { MXN: 'CLABE', USD: 'ABA', CAD: 'CPA' } as const;
  return (types as Record<string, 'CLABE' | 'ABA' | 'CPA'>)[currency];
}

/** Merge freshly issued references into the instructions, matched by product. */
export function withIssuedReferences(
  data: FundInstructions,
  issued: FundableProduct[],
): FundInstructions {
  if (issued.length === 0) return data;
  const byProduct = new Map(issued.map((p) => [p.product_id, p]));
  return {
    ...data,
    fundable_products: (data.fundable_products ?? []).map((p) => byProduct.get(p.product_id) ?? p),
  };
}

/**
 * Normalised reference number for a pending transfer, or `null` when it is
 * missing/blank.
 *
 * A pending transfer is only safe to act on once it carries a non-empty
 * reference: the client MUST copy it into the bank "Concept" / "Memo" field
 * so the incoming wire reconciles to their account. Without it, money sent
 * to the destination account cannot be matched and would land in a manual
 * investigation queue (the exact reconciliation gap a fresh QA flagged).
 *
 * `investments.deposit_requests.reference_number` is NOT NULL at the DB
 * level, so in practice this is always a string. The guard exists so the UI
 * fails SAFE if that invariant is ever broken (bad migration, malformed
 * response): the caller withholds the destination details and tells the
 * client to wait, instead of inviting an untracked transfer.
 *
 * Trims surrounding whitespace so a blank-but-present value is treated the
 * same as a missing one.
 */
export function transferReference(
  deposit: Pick<PendingFundRequest, 'reference_number'>,
): string | null {
  const ref = deposit.reference_number?.trim();
  return ref || null;
}

/** Said BEFORE the first transfer (S-27): where a contribution may come from, what happens to
 *  money that comes from anywhere else, and that QMI may decline an operation. Plain, with no
 *  mention of any control. Wording: VALIDAR CON ABOGADO. */
export const SOURCE_ACCOUNT_NOTICE =
  'Send your contributions only from a bank account in your name that you have registered here. ' +
  'Money that comes from any other account is not credited: it goes back to the account it ' +
  'came from, and the bank’s fee for returning it is taken from the amount returned. We may ask ' +
  'you for documents about where the money comes from, and we may decline an operation.';

/** Shown with the funding instructions (0082). The firm credits only payments that are
 *  final when they settle; the same sentence the server sends when it refuses one. */
export const ACCEPTED_METHODS_NOTICE =
  'We accept bank wires only. We do not accept checks, ACH, cards or payments pulled ' +
  'from your account, because those payments can be reversed after they arrive. A ' +
  'payment sent another way is returned to the account it came from, and the bank fee ' +
  'for returning it is taken from the amount returned. Send the transfer only from a ' +
  'bank account in your own name.';
